/**
 * <baton-pricing-calculator> — the Baton configurator + curl generator.
 *
 * Brief 121: ONE generic-baton configurator (no "Shape" toggle). A baton is a
 * set of independent controls; an optional row of use-case cards prefills them;
 * a persistent "Your baton" pane reads the configuration back in plain English
 * with the live price; the full-width curl is the artifact you leave with. ONE
 * published price — every configuration is priced by `POST /v1/baton/quote` on
 * its generic shape (no catalog flip); the curl always emits the explicit
 * generic form, so what's shown equals what's ordered.
 *
 * Brief 121 Step-6 re-QA: built to the operator-approved mockup — use-case
 * cards with icons + preset descriptors; a layout reflow (only Write behavior +
 * "Your baton" are two-column; Capacity/Sharing/Lifecycle/Trust/curl run full
 * width); one-line write-behavior descriptions; a promoted full-width Capacity
 * panel; Lifecycle as a burn checkbox + soft/hard radios.
 *
 * Attributes: `api-base` (DEFAULT '' = same-origin), `curl-base` (DEFAULT
 * https://api.relaystation.ai). Readback API (Brief 112): `getShape()` +
 * `baton-shape-change`.
 */

interface CatalogRow {
  preset: string;
  tier: string;
  // Brief 122 — /v1/baton/prices no longer returns priceMicros (bundle refs
  // are engine-priced via /quote); the calculator reads shape + description.
  maxSizeBytes: string;
  durationSeconds: string;
  maxEgressBytes: string;
  writesAllowed: number | null;
  readsAllowed: number | null;
  headlineCapacity?: string;
  description?: string | null;
}

interface QuoteResponse {
  kind: 'bundle' | 'custom';
  amountMicros: string;
  serviceKey: string;
  breakdown?: Record<string, unknown>;
}

export type TokenScope = 'read' | 'write' | 'read_write';

export interface BatonTokenSpec {
  type: TokenScope;
  reads_allowed?: number;
  expires_at?: string;
}

export interface BatonFlags {
  hashChaining?: boolean;
  witness?: boolean;
}

export interface BatonShape {
  preset: string;
  customShape: {
    sizeBytes: number;
    durationSeconds: number;
    maxEgressBytes: number;
  };
  contentEncoding?: 'binary';
  tokens?: BatonTokenSpec[];
  flags?: BatonFlags;
  onExpiration?: 'hard_delete' | 'tombstone_then_delete';
  deleteOnAllTokensConsumed?: boolean;
}

export interface BatonShapeChangeDetail {
  shape: BatonShape;
  quote?: { amountMicros: string; appliedFloor: boolean };
}

type CurlMode = 'wallet' | 'api' | 'mcp';

interface WriteBehavior {
  id: string;
  label: string;
  desc: string;
  preset: string;
  contentEncoding?: 'binary';
  chained: boolean;
}

const WRITE_BEHAVIORS: WriteBehavior[] = [
  { id: 'single_object', label: 'Single object', desc: 'A file, image, video, or any binary payload.', preset: 'checkpoint', contentEncoding: 'binary', chained: false },
  { id: 'append', label: 'Append', desc: 'A text log you add entries to over time.', preset: 'scratchpad', chained: false },
  { id: 'append_chained', label: 'Append, hash-chained', desc: 'An append log, hash-linked end to end — tamper-evident.', preset: 'ledger', chained: true },
  { id: 'overwrite', label: 'Overwrite', desc: 'A text file you replace in full; only the latest is kept.', preset: 'checkpoint', chained: false },
];

interface UseCaseCard {
  id: string;
  label: string;
  descriptor: string;
  icon: string;
  /** Which preset's C1 description to show — keyed to the preconfig NAME. */
  descPreset: string;
  prefillPreset: string;
  writeBehavior: string;
  tokens?: { count: number; scope: TokenScope; reads?: number };
  burn?: boolean;
}

const USE_CASE_CARDS: UseCaseCard[] = [
  { id: 'store', label: 'Store a file', descriptor: 'Drop', icon: 'file', descPreset: 'drop', prefillPreset: 'drop', writeBehavior: 'single_object' },
  { id: 'share', label: 'Share a file', descriptor: 'Pass', icon: 'share', descPreset: 'pass', prefillPreset: 'pass', writeBehavior: 'single_object', tokens: { count: 1, scope: 'read', reads: 1 }, burn: true },
  { id: 'savestate', label: 'Save state', descriptor: 'Checkpoint', icon: 'floppy', descPreset: 'checkpoint', prefillPreset: 'checkpoint', writeBehavior: 'overwrite' },
  { id: 'collab', label: 'Collaborate', descriptor: 'Scratchpad', icon: 'users', descPreset: 'scratchpad', prefillPreset: 'scratchpad', writeBehavior: 'append', tokens: { count: 2, scope: 'read_write' } },
  { id: 'ledger', label: 'Audited Ledger', descriptor: 'Ledgers, Contracts', icon: 'list', descPreset: 'ledger', prefillPreset: 'ledger', writeBehavior: 'append_chained' },
];

/** Small inline icons (Tabler-style; self-contained — no icon-font dependency
 *  since the element ships into hosts that don't load one). */
const ICON_PATHS: Record<string, string> = {
  file: '<path d="M14 3v4a1 1 0 0 0 1 1h4"/><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z"/>',
  share: '<circle cx="6" cy="12" r="2.5"/><circle cx="18" cy="6" r="2.5"/><circle cx="18" cy="18" r="2.5"/><path d="M8.7 10.8l6.6-3.6"/><path d="M8.7 13.2l6.6 3.6"/>',
  floppy: '<path d="M6 4h9l5 5v10a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 2-1z"/><circle cx="12" cy="14" r="2"/><path d="M8 4v4h6V4"/>',
  users: '<circle cx="9" cy="7" r="3"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/><path d="M16 3.2a4 4 0 0 1 0 7.6"/><path d="M21 21v-2a4 4 0 0 0-3-3.85"/>',
  list: '<path d="M11 6h9"/><path d="M11 12h9"/><path d="M11 18h9"/><path d="M3.5 6l1.5 1.5L8 4.5"/><path d="M3.5 12l1.5 1.5L8 10.5"/><path d="M3.5 18l1.5 1.5L8 16.5"/>',
};

function icon(id: string): string {
  return `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="shrink-0 text-muted-foreground">${ICON_PATHS[id] ?? ''}</svg>`;
}

const TIER_ORDER = ['femto', 'pico', 'nano', 'micro', 'standard', 'big'];
const PREFILL_TIER = 'nano';
const MAX_TOKENS_PER_BATON = 100;
const QUOTE_DEBOUNCE_MS = 450;

function microsToUsd(micros: string): string {
  const n = Number(micros);
  if (!Number.isFinite(n)) return micros;
  const dollars = n / 1_000_000;
  return `$${dollars.toLocaleString(undefined, {
    minimumFractionDigits: dollars < 1 ? 4 : 2,
    maximumFractionDigits: 6,
  })}`;
}

function humanBytes(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toLocaleString()} GB`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toLocaleString()} MB`;
  if (n >= 1_000) return `${(n / 1_000).toLocaleString()} KB`;
  return `${n.toLocaleString()} B`;
}

function humanDays(days: number): string {
  if (!Number.isFinite(days)) return String(days);
  if (days >= 365 && days % 365 === 0) return `${days / 365} year${days === 365 ? '' : 's'}`;
  return `${days.toLocaleString()} day${days === 1 ? '' : 's'}`;
}

/** "An" before a vowel sound, else "A" (approximation by leading letter). */
function indefiniteArticle(word: string): string {
  return /^[aeiou]/i.test(word.trim()) ? 'An' : 'A';
}

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&quot;',
  );
}

export class BatonPricingCalculator extends HTMLElement {
  private apiBase = '';
  private curlBase = 'https://api.relaystation.ai';
  private catalog: CatalogRow[] = [];
  private quoteTimer: ReturnType<typeof setTimeout> | undefined;
  private lastCurlBody: Record<string, unknown> | null = null;
  /** Tracks whether the write behavior was hash-chained on the previous
   *  applyDependencies pass, so we can reset the auto-locked witness on the
   *  transition *away from* chained (brief 124). Default radio is single_object
   *  (not chained), so the initial value is false. */
  private prevChained = false;

  connectedCallback(): void {
    this.apiBase = (this.getAttribute('api-base') ?? this.apiBase).replace(/\/$/, '');
    this.curlBase = (this.getAttribute('curl-base') ?? this.curlBase).replace(/\/$/, '');
    this.renderShell();
    void this.loadCatalog();
  }

  disconnectedCallback(): void {
    if (this.quoteTimer) clearTimeout(this.quoteTimer);
  }

  private q<T extends HTMLElement>(sel: string): T {
    return this.querySelector(sel) as T;
  }

  // ── render ──────────────────────────────────────────────────────────────

  private renderShell(): void {
    const cards = USE_CASE_CARDS.map(
      (c) =>
        `<button type="button" data-card="${esc(c.id)}"
           class="rounded-md border border-border bg-background px-3 py-2 text-left text-xs hover:border-primary">
           <span class="flex items-center gap-1.5">${icon(c.icon)}<span class="font-medium">${esc(c.label)}</span></span>
           <span class="mt-0.5 block text-muted-foreground">${esc(c.descriptor)}</span>
         </button>`,
    ).join('');

    const writeRows = WRITE_BEHAVIORS.map(
      (w, i) =>
        `<label class="flex cursor-pointer items-baseline gap-2 rounded-md border border-border bg-background p-2 text-sm has-[:checked]:border-primary">
           <input type="radio" name="wb" data-wb value="${esc(w.id)}" ${i === 0 ? 'checked' : ''} class="self-start mt-1" />
           <span><span class="font-medium">${esc(w.label)}</span>
             <span class="text-xs text-muted-foreground"> — ${esc(w.desc)}</span></span>
         </label>`,
    ).join('');

    const sharingTrustCapacity = `
      <section class="space-y-2 rounded-md border border-border bg-primary/5 p-3">
        <div class="text-xs uppercase tracking-wide text-muted-foreground">Capacity — the main lever on price</div>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label class="text-sm space-y-1"><span class="block text-muted-foreground">Size (KB)</span>
            <input data-size type="number" min="1" value="256" class="w-full rounded border border-border bg-background px-2 py-1" /></label>
          <label class="text-sm space-y-1"><span class="block text-muted-foreground">Duration (days)</span>
            <input data-days type="number" min="1" value="7" class="w-full rounded border border-border bg-background px-2 py-1" /></label>
          <label class="text-sm space-y-1"><span class="block text-muted-foreground">Egress (MB)</span>
            <input data-egress type="number" min="1" value="5" class="w-full rounded border border-border bg-background px-2 py-1" /></label>
        </div>
      </section>
      <p class="!m-0 text-xs text-muted-foreground">A single write over 3 MB uploads via a presigned URL the create returns.</p>

      <section class="space-y-2">
        <div class="text-xs uppercase tracking-wide text-muted-foreground">Sharing</div>
        <div class="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <label class="text-sm space-y-1"><span class="block text-muted-foreground">Tokens (0–${MAX_TOKENS_PER_BATON})</span>
            <input data-tokens type="number" min="0" max="${MAX_TOKENS_PER_BATON}" value="0" class="w-full rounded border border-border bg-background px-2 py-1" /></label>
          <label class="text-sm space-y-1"><span class="block text-muted-foreground">Token scope</span>
            <select data-token-scope class="w-full rounded border border-border bg-background px-2 py-1">
              <option value="read">read</option><option value="write">write</option><option value="read_write">read_write</option>
            </select></label>
          <label class="text-sm space-y-1"><span class="block text-muted-foreground">Reads allowed / token</span>
            <input data-token-reads type="number" min="1" placeholder="uncapped" class="w-full rounded border border-border bg-background px-2 py-1" /></label>
          <label class="text-sm space-y-1"><span class="block text-muted-foreground">Token expiry</span>
            <input data-token-expiry type="datetime-local" class="w-full rounded border border-border bg-background px-2 py-1" /></label>
        </div>
        <p class="!m-0 text-xs text-muted-foreground">Set as a group — every token gets these. For per-token control, mint them individually via the API.</p>
      </section>

      <section class="space-y-2">
        <div class="text-xs uppercase tracking-wide text-muted-foreground">Lifecycle</div>
        <label class="flex items-center gap-2 text-sm"><input data-burn type="checkbox" /> Burn after reading — delete once every token's read cap is reached</label>
        <label class="flex items-center gap-2 text-sm"><input type="radio" name="disposal" data-disposal value="soft" checked /> Soft delete — 72-hour grace period</label>
        <label class="flex items-center gap-2 text-sm"><input type="radio" name="disposal" data-disposal value="hard" /> Hard delete — no grace period</label>
      </section>

      <section class="space-y-2">
        <div class="text-xs uppercase tracking-wide text-muted-foreground">Trust</div>
        <label class="flex items-start gap-2 text-sm"><input data-witness type="checkbox" class="mt-1" />
          <span>Document-witness — Relaystation signs your baton and keeps its content hash on file for 7 years; prove the content is unchanged any time via the API or verify the signature yourself offline.</span></label>
      </section>`;

    this.innerHTML = `
      <div class="space-y-4">
        <div data-status class="text-xs text-muted-foreground">Loading the live catalog…</div>

        <div class="rounded-md border border-border bg-muted/40 p-3 space-y-2">
          <div class="text-xs uppercase tracking-wide text-muted-foreground">Start from a use case (optional)</div>
          <div class="grid grid-cols-2 gap-2 sm:grid-cols-5">${cards}</div>
          <div class="flex items-center justify-between">
            <p class="!m-0 text-xs text-muted-foreground">A starting point — every control below is still yours to change.</p>
            <button type="button" data-clear class="text-xs text-primary hover:underline">Clear</button>
          </div>
          <div data-card-desc class="hidden rounded border border-border bg-background p-2 text-xs leading-5"></div>
        </div>

        <!-- Two-column row: Write behavior (wide) + Your baton (narrow) -->
        <div class="grid gap-4 md:grid-cols-[1.6fr_1fr]">
          <section class="space-y-2">
            <div class="text-xs uppercase tracking-wide text-muted-foreground">Write behavior</div>
            <div class="grid gap-2">${writeRows}</div>
          </section>
          <div class="rounded-md border border-border bg-muted/40 p-4 space-y-3">
            <div class="text-xs uppercase tracking-wide text-muted-foreground">Your baton</div>
            <p data-summary class="!m-0 text-sm leading-6"></p>
            <div class="border-t border-border pt-3">
              <span class="text-xs uppercase tracking-wide text-muted-foreground">Price</span>
              <div class="flex items-baseline gap-2">
                <span data-price class="font-mono text-lg font-semibold">—</span>
                <span data-floor class="text-xs text-muted-foreground"></span>
              </div>
            </div>
          </div>
        </div>

        <!-- Full-width controls -->
        <div class="space-y-4">${sharingTrustCapacity}</div>

        <!-- Curl (full width) -->
        <div class="space-y-1">
          <div class="flex items-center justify-between gap-2">
            <span class="text-xs uppercase tracking-wide text-muted-foreground">Copy-paste call</span>
            <div class="flex items-center gap-2">
              <select data-curl-mode class="rounded border border-border bg-background px-1.5 py-0.5 text-xs">
                <option value="wallet">Wallet (x402 — no account)</option>
                <option value="api">API key</option>
                <option value="mcp">MCP (tool call)</option>
              </select>
              <button data-copy type="button" class="text-xs text-primary hover:underline">Copy</button>
            </div>
          </div>
          <pre data-curl class="!m-0 overflow-x-auto rounded border border-border bg-background p-2 text-xs leading-5 whitespace-pre-wrap"></pre>
          <p data-curl-note class="!m-0 text-xs text-muted-foreground"></p>
        </div>
      </div>
    `;

    for (const c of USE_CASE_CARDS) {
      this.q<HTMLButtonElement>(`[data-card="${c.id}"]`).addEventListener('click', () => this.applyCard(c));
    }
    this.q<HTMLButtonElement>('[data-clear]').addEventListener('click', () => this.clearCard());
    for (const el of Array.from(this.querySelectorAll<HTMLInputElement>('[data-wb], [data-disposal]'))) {
      el.addEventListener('change', () => this.onFieldChange());
    }
    for (const sel of [
      '[data-size]', '[data-days]', '[data-egress]',
      '[data-tokens]', '[data-token-scope]', '[data-token-reads]', '[data-token-expiry]',
      '[data-burn]', '[data-witness]',
    ]) {
      const el = this.querySelector<HTMLElement>(sel);
      if (!el) continue;
      el.addEventListener('input', () => this.onFieldChange());
      el.addEventListener('change', () => this.onFieldChange());
    }
    this.q<HTMLSelectElement>('[data-curl-mode]').addEventListener('change', () => this.renderCurl());
    this.q<HTMLButtonElement>('[data-copy]').addEventListener('click', () => this.copyCurl());
  }

  private async loadCatalog(): Promise<void> {
    const status = this.q<HTMLElement>('[data-status]');
    try {
      const res = await fetch(`${this.apiBase}/v1/baton/prices`, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`prices ${res.status}`);
      const data = (await res.json()) as { bundles: CatalogRow[] };
      this.catalog = data.bundles ?? [];
      status.textContent = '';
      status.classList.add('hidden');
      this.onFieldChange();
    } catch (err) {
      status.classList.remove('hidden');
      status.textContent = `Couldn't load the live catalog (${String(err)}).`;
    }
  }

  // ── use-case cards ────────────────────────────────────────────────────────

  private rowForPrefill(preset: string): CatalogRow | undefined {
    const rows = this.catalog
      .filter((r) => r.preset === preset)
      .sort((a, b) => (TIER_ORDER.indexOf(a.tier) + 1 || 99) - (TIER_ORDER.indexOf(b.tier) + 1 || 99));
    return rows.find((r) => r.tier === PREFILL_TIER) ?? rows[0];
  }

  private applyCard(card: UseCaseCard): void {
    const wb = this.querySelector<HTMLInputElement>(`[data-wb][value="${card.writeBehavior}"]`);
    if (wb) wb.checked = true;
    const row = this.rowForPrefill(card.prefillPreset);
    if (row) {
      this.q<HTMLInputElement>('[data-size]').value = String(Math.round(Number(row.maxSizeBytes) / 1000));
      this.q<HTMLInputElement>('[data-days]').value = String(Math.round(Number(row.durationSeconds) / 86400));
      this.q<HTMLInputElement>('[data-egress]').value = String(Math.round(Number(row.maxEgressBytes) / 1_000_000));
    }
    this.q<HTMLInputElement>('[data-tokens]').value = String(card.tokens?.count ?? 0);
    this.q<HTMLSelectElement>('[data-token-scope]').value = card.tokens?.scope ?? 'read';
    this.q<HTMLInputElement>('[data-token-reads]').value = card.tokens?.reads ? String(card.tokens.reads) : '';
    this.q<HTMLInputElement>('[data-token-expiry]').value = '';
    this.q<HTMLInputElement>('[data-burn]').checked = card.burn ?? false;
    const soft = this.querySelector<HTMLInputElement>('[data-disposal][value="soft"]');
    if (soft) soft.checked = true;
    this.q<HTMLInputElement>('[data-witness]').checked = false;
    const descEl = this.q<HTMLElement>('[data-card-desc]');
    const desc = this.catalog.find((r) => r.preset === card.descPreset)?.description ?? null;
    if (desc) {
      descEl.textContent = desc;
      descEl.classList.remove('hidden');
    } else {
      descEl.classList.add('hidden');
    }
    this.onFieldChange();
  }

  private clearCard(): void {
    this.q<HTMLElement>('[data-card-desc]').classList.add('hidden');
  }

  // ── state + dependencies ──────────────────────────────────────────────────

  private currentBehavior(): WriteBehavior {
    const checked = this.querySelector<HTMLInputElement>('[data-wb]:checked');
    return WRITE_BEHAVIORS.find((w) => w.id === checked?.value) ?? WRITE_BEHAVIORS[0]!;
  }

  private disposal(): 'soft' | 'hard' {
    return this.querySelector<HTMLInputElement>('[data-disposal]:checked')?.value === 'hard' ? 'hard' : 'soft';
  }

  /** Inline dependencies: hash-chained → witness auto-locked; burn + tokens≥1
   *  → token scope `read` + reads ≥ 1. */
  private applyDependencies(): void {
    const behavior = this.currentBehavior();
    const witnessEl = this.q<HTMLInputElement>('[data-witness]');
    if (behavior.chained) {
      witnessEl.checked = true;
      witnessEl.disabled = true;
    } else {
      // Transition away from hash-chained: the witness was auto-locked-on, so
      // clear the lingering check rather than carry it into a non-chained baton.
      // Any other transition (e.g. single_object → append) leaves the user's
      // explicit witness choice untouched (brief 124).
      if (this.prevChained) witnessEl.checked = false;
      witnessEl.disabled = false;
    }
    this.prevChained = behavior.chained;

    const scopeEl = this.q<HTMLSelectElement>('[data-token-scope]');
    const countEl = this.q<HTMLInputElement>('[data-tokens]');
    const readsEl = this.q<HTMLInputElement>('[data-token-reads]');
    const interlock = this.q<HTMLInputElement>('[data-burn]').checked && (Number(countEl.value) || 0) >= 1;
    if (interlock) {
      scopeEl.value = 'read';
      scopeEl.disabled = true;
      readsEl.required = true;
      if (readsEl.value.trim() === '' || Number(readsEl.value) < 1) readsEl.value = '1';
    } else {
      scopeEl.disabled = false;
      readsEl.required = false;
    }
  }

  private onFieldChange(): void {
    if (!this.querySelector('[data-wb]')) return;
    this.applyDependencies();
    this.renderSummary();
    this.emitShapeChange();
    this.scheduleQuote();
  }

  // ── shape ──────────────────────────────────────────────────────────────────

  private readTokens(): BatonTokenSpec[] | undefined {
    const count = Math.min(Number(this.q<HTMLInputElement>('[data-tokens]').value) || 0, MAX_TOKENS_PER_BATON);
    if (count < 1) return undefined;
    const scope = this.q<HTMLSelectElement>('[data-token-scope]').value as TokenScope;
    const spec: BatonTokenSpec = { type: scope };
    const readsRaw = this.q<HTMLInputElement>('[data-token-reads]').value.trim();
    if (readsRaw !== '') {
      const r = Number(readsRaw);
      if (Number.isFinite(r) && r >= 1) spec.reads_allowed = Math.round(r);
    }
    const expiryRaw = this.q<HTMLInputElement>('[data-token-expiry]').value.trim();
    if (expiryRaw !== '') {
      const d = new Date(expiryRaw);
      if (!Number.isNaN(d.getTime())) spec.expires_at = d.toISOString();
    }
    if (this.q<HTMLInputElement>('[data-burn]').checked) {
      spec.type = 'read';
      if (!spec.reads_allowed || spec.reads_allowed < 1) spec.reads_allowed = 1;
    }
    return Array.from({ length: Math.round(count) }, () => ({ ...spec }));
  }

  getShape(): BatonShape {
    const behavior = this.currentBehavior();
    const kb = Number(this.q<HTMLInputElement>('[data-size]').value) || 0;
    const days = Number(this.q<HTMLInputElement>('[data-days]').value) || 0;
    const mb = Number(this.q<HTMLInputElement>('[data-egress]').value) || 0;
    const shape: BatonShape = {
      preset: behavior.preset,
      customShape: {
        sizeBytes: Math.round(kb * 1000),
        durationSeconds: Math.round(days * 86400),
        maxEgressBytes: Math.round(mb * 1_000_000),
      },
      onExpiration: this.disposal() === 'hard' ? 'hard_delete' : 'tombstone_then_delete',
      deleteOnAllTokensConsumed: this.q<HTMLInputElement>('[data-burn]').checked,
    };
    if (behavior.contentEncoding) shape.contentEncoding = behavior.contentEncoding;
    const tokens = this.readTokens();
    if (tokens) shape.tokens = tokens;
    if (this.q<HTMLInputElement>('[data-witness]').checked) shape.flags = { witness: true };
    return shape;
  }

  private emitShapeChange(quote?: QuoteResponse): void {
    if (!this.querySelector('[data-wb]')) return;
    const detail: BatonShapeChangeDetail = { shape: this.getShape() };
    if (quote) {
      detail.quote = { amountMicros: quote.amountMicros, appliedFloor: quote.breakdown?.['appliedFloor'] === true };
    }
    this.dispatchEvent(new CustomEvent<BatonShapeChangeDetail>('baton-shape-change', { detail, bubbles: true }));
  }

  // ── "Your baton" summary ───────────────────────────────────────────────────

  private renderSummary(): void {
    const behavior = this.currentBehavior();
    const s = this.getShape();
    const label = behavior.label.toLowerCase();
    const parts: string[] = [];
    parts.push(
      `${indefiniteArticle(label)} ${label} baton — ${humanBytes(s.customShape.sizeBytes)}, ` +
        `${humanDays(Math.round(s.customShape.durationSeconds / 86400))}, a ${humanBytes(s.customShape.maxEgressBytes)} read budget.`,
    );
    if (s.tokens && s.tokens.length > 0) {
      const t = s.tokens[0]!;
      const cap = t.reads_allowed ? `, capped at ${t.reads_allowed} read${t.reads_allowed === 1 ? '' : 's'} each` : '';
      const exp = t.expires_at ? ', with a per-token expiry' : '';
      parts.push(`Shared with ${s.tokens.length} ${t.type} token${s.tokens.length === 1 ? '' : 's'}${cap}${exp}.`);
    }
    if (s.deleteOnAllTokensConsumed) parts.push('It burns itself once every token is used up.');
    parts.push(s.onExpiration === 'hard_delete' ? 'Hard delete (no grace).' : 'Soft delete (72h grace).');
    if (s.flags?.witness) parts.push('Document-witnessed.');
    this.q<HTMLElement>('[data-summary]').textContent = parts.join(' ');
  }

  // ── pricing ─────────────────────────────────────────────────────────────────

  private buildQuoteBody(): Record<string, unknown> {
    const s = this.getShape();
    const body: Record<string, unknown> = { preset: s.preset, customShape: s.customShape };
    if (s.flags) body.flags = s.flags;
    return body;
  }

  private scheduleQuote(): void {
    if (this.quoteTimer) clearTimeout(this.quoteTimer);
    this.quoteTimer = setTimeout(() => void this.runQuote(), QUOTE_DEBOUNCE_MS);
  }

  private async runQuote(): Promise<void> {
    const status = this.q<HTMLElement>('[data-status]');
    try {
      const res = await fetch(`${this.apiBase}/v1/baton/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(this.buildQuoteBody()),
      });
      if (!res.ok) throw new Error(`quote ${res.status}`);
      const quote = (await res.json()) as QuoteResponse;
      this.q<HTMLElement>('[data-price]').textContent = microsToUsd(quote.amountMicros);
      this.q<HTMLElement>('[data-floor]').textContent =
        quote.breakdown?.['appliedFloor'] === true ? '(network minimum)' : '';
      this.lastCurlBody = {
        ...(this.getShape() as unknown as Record<string, unknown>),
        content: '<your-payload>',
      };
      this.renderCurl();
      status.classList.add('hidden');
      this.emitShapeChange(quote);
    } catch (err) {
      status.classList.remove('hidden');
      status.textContent = `Couldn't price that (${String(err)}).`;
    }
  }

  // ── curl ─────────────────────────────────────────────────────────────────────

  private renderCurl(): void {
    if (!this.lastCurlBody) return;
    const mode = this.q<HTMLSelectElement>('[data-curl-mode]').value as CurlMode;
    this.q<HTMLElement>('[data-curl]').textContent = this.buildCurl(this.lastCurlBody, mode);
    this.q<HTMLElement>('[data-curl-note]').textContent =
      mode === 'wallet'
        ? 'Wallet path (the lodestone): no account needed. X-Payment carries your base64 x402 (EIP-3009) authorization — the price above is the USDC amount to authorize. Any tokens come back in the response.'
        : mode === 'mcp'
          ? 'MCP path: call create_baton from any MCP client (Claude, Cursor, …) connected to api.relaystation.ai/mcp — the connector authenticates your account; same balance, same price as the API-key path.'
          : 'API-key path: for a signed-in account (rs_live_… key).';
  }

  private buildCurl(body: Record<string, unknown>, mode: CurlMode): string {
    if (mode === 'mcp') {
      // MCP "call" mode: the create_baton tool call instead of curl. Same body.
      return [
        '// Connect api.relaystation.ai/mcp, then call:',
        `create_baton(${JSON.stringify(body, null, 2)})`,
      ].join('\n');
    }
    const json = JSON.stringify(body).replace(/'/g, `'\\''`);
    const authHeader =
      mode === 'wallet'
        ? `  -H "X-Payment: <base64 x402 authorization>" \\`
        : `  -H "Authorization: Bearer rs_live_..." \\`;
    return [
      `curl -X POST ${this.curlBase}/v1/baton \\`,
      authHeader,
      `  -H "Idempotency-Key: $(uuidgen)" \\`,
      `  -H "Content-Type: application/json" \\`,
      `  -d '${json}'`,
    ].join('\n');
  }

  private copyCurl(): void {
    const text = this.q<HTMLElement>('[data-curl]').textContent ?? '';
    void navigator.clipboard?.writeText(text);
    const btn = this.q<HTMLButtonElement>('[data-copy]');
    const prev = btn.textContent;
    btn.textContent = 'Copied';
    setTimeout(() => { btn.textContent = prev; }, 1200);
  }
}

if (
  typeof customElements !== 'undefined' &&
  !customElements.get('baton-pricing-calculator')
) {
  customElements.define('baton-pricing-calculator', BatonPricingCalculator);
}
